import React from "react";
import { Reveal } from "../Reveal";
import stinePhoto from "../../assets/stine.webp";

const methods = [
  "Coaching",
  "Acceptance and Commitment Therapy (ACT)",
  "Mindfulness",
  "Metakognitiv psykologi",
  "Positiv psykologi",
  "Idræts- og performancepsykologi",
  "Åndedræt",
  "3P (De Tre Principper)",
];

const SectionEyebrow = () => <div className="mb-6 h-0.5 w-12 bg-[#852936]" />;

const today = ["Coach", "Underviser", "Facilitator"];
const background = [
  "ICF-certificeret coach",
  "Cand.comm. i Kommunikation & Performance Design",
  "Projektleder i Deloitte",
];

const PreviewBelief = () => (
  <section id="om" className="relative overflow-hidden" data-testid="preview-section-om">
    <div className="hero-nuance relative py-16 sm:py-24">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      {/* Det, jeg tror på — billede venstre, tekst højre */}
      <div className="grid gap-10 lg:grid-cols-12 lg:items-start lg:gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative lg:sticky lg:top-28">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#A9B5A0]/15" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] preview-shadow-lift" data-testid="preview-belief-image">
                <img src={stinePhoto} alt="Stine Eskesen" className="h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <SectionEyebrow />
            <h2 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-[#111827] sm:text-5xl md:text-6xl" data-testid="preview-heading-tror">
              Det, jeg tror på
            </h2>
            <p className="mt-8 max-w-2xl font-display text-xl font-normal italic leading-snug text-[#852936] sm:text-2xl">
              Mental frihed til at være, udvikle og præstere
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 space-y-4 text-[17px] leading-relaxed text-[#111827]/70">
              <p>
                Jeg tror på, at udvikling begynder med nysgerrighed. Nysgerrighed på os selv, vores
                tanker, følelser og mønstre – og på alt det, der sker i os, særligt når livet føles lidt
                svært. Det handler om at møde os selv og verden med et åbent sind – et beginner’s mind –
                hvor vi ikke på forhånd tror, at vi kender alle svarene.
              </p>
              <p>
                Vores oplevelse formes i høj grad af vores tanker. Når vi forstår, at vores tanker er
                noget, vi har – ikke noget, vi er – åbner der sig nye muligheder. Vi behøver ikke kæmpe
                imod alt det, vi tænker og føler, men kan give det plads uden at lade det definere eller
                begrænse os. Herfra kan vi vende tilbage til nuet og handle mere bevidst i tråd med det,
                der er vigtigt for os. Vi kan stå stærkere i os selv, gå med vores udvikling og præstere
                som dem, vi er – med større indre ro, mere mental frihed og masser af mod.
              </p>
              <p>
                Gennem samtaler, workshops og undervisning hjælper jeg mennesker og fællesskaber i
                præstationsmiljøer og i livet generelt med at udvikle sig – at være og præstere når det
                gælder med mere mental frihed.
              </p>
              <p>
                Læs mere om mine metoder og tilgange nedenfor og om samtale- og undervisningsforløb i
                menuen.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
    </div>

    <div className="relative bg-[#F4F1EC] py-16 sm:py-24">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      {/* Sådan arbejder jeg + Min baggrund — side om side */}
      <div className="grid gap-12 lg:grid-cols-2 lg:items-start lg:gap-16">
        {/* Sådan arbejder jeg */}
        <div data-testid="preview-subsection-metode">
          <Reveal>
            <SectionEyebrow />
            <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight text-[#111827] sm:text-4xl md:text-5xl">
              Sådan arbejder jeg
            </h2>
            <p className="mt-6 font-display text-xl font-normal italic leading-snug text-[#852936] sm:text-2xl">
              Tilgange, jeg er inspireret af
            </p>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 text-[17px] leading-relaxed text-[#111827]/70">
              Jeg ser mennesket og møder dem, hvor de er. Derfor kombinerer jeg psykologiske retninger,
              perspektiver og redskaber, der giver mening i den konkrete situation. Jeg er inspireret af
              og gør brug af følgende:
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {methods.map((m) => (
                <span
                  key={m}
                  className="rounded-full border border-[#852936]/15 bg-white px-4 py-2 text-[15px] font-medium text-[#6E1F2B] preview-shadow-soft"
                  data-testid={`preview-method-${m}`}
                >
                  {m}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Min baggrund */}
        <div data-testid="preview-subsection-baggrund">
          <Reveal>
            <SectionEyebrow />
            <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight text-[#111827] sm:text-4xl md:text-5xl">
              Min baggrund
            </h2>
            <p className="mt-6 font-display text-xl font-normal italic leading-snug text-[#852936] sm:text-2xl">
              Erfaring, der har formet mig
            </p>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 text-[17px] leading-relaxed text-[#111827]/70">
              Jeg er vokset op i et både elitepræget og pædagogisk miljø, hvorfor læring om og
              nysgerrighed på psykologi og menneskelig udvikling har været en del af min opvækst og mit
              hverdagsliv. Derfor er det også naturligt for mig at arbejde med udvikling, trivsel og
              præstation.
            </p>
            <div className="mt-8 rounded-3xl border border-black/[0.06] bg-white p-6 preview-shadow-soft" data-testid="preview-box-baggrund-kort">
              <div data-testid="preview-box-idag">
                <span className="eyebrow mb-3 block text-[11px] font-semibold text-[#852936]">I dag</span>
                <ul className="space-y-2 text-[15px] text-[#111827]/75">
                  {today.map((t) => (
                    <li key={t} className="flex gap-3">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#852936]" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 border-t border-black/[0.07] pt-6" data-testid="preview-box-baggrund">
                <span className="eyebrow mb-3 block text-[11px] font-semibold text-[#852936]">Baggrund</span>
                <ul className="space-y-2 text-[15px] text-[#111827]/75">
                  {background.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#852936]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
    </div>
  </section>
);

export default PreviewBelief;
